import React, { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Tiny pulsing backend health indicator. Polls /api/health/routes and shows
 * a green dot + route count when all routers are mounted, red on failure.
 * Mounted on the MainMenu (and only rendered for wallet-connected users).
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const POLL_MS = 30_000;

const BackendStatusBadge = () => {
  const [status, setStatus] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/health/routes`, { timeout: 8000 });
        if (cancelled) return;
        setStatus({
          loading: false,
          ok: !!res.data?.all_ok,
          total: res.data?.total_routes_mounted || 0,
          missing: Object.entries(res.data?.groups || {})
            .filter(([, v]) => !v.ok)
            .map(([k]) => k),
        });
      } catch (e) {
        if (cancelled) return;
        setStatus({ loading: false, ok: false, total: 0, error: true });
      }
    };
    fetchStatus();
    const id = setInterval(fetchStatus, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (status.loading) return null;

  const ok = status.ok;
  const dotColor = ok ? 'bg-[#14F195]' : 'bg-[#FF3B30]';
  const borderColor = ok ? 'border-[#14F195]/40' : 'border-[#FF3B30]/40';
  const textColor = ok ? 'text-[#14F195]' : 'text-[#FF3B30]';
  const label = status.error
    ? 'Backend UNREACHABLE'
    : ok
      ? `Backend OK · ${status.total} routes`
      : `Backend DEGRADED · missing: ${(status.missing || []).join(', ')}`;

  return (
    <div
      className={`absolute top-4 left-4 z-20 flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1a1a1a]/80 border ${borderColor} backdrop-blur-sm`}
      data-testid="backend-status-badge"
      title={label}
    >
      <span className={`relative flex h-2 w-2`}>
        {ok && (
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${dotColor} opacity-75`}></span>
        )}
        <span className={`relative inline-flex rounded-full h-2 w-2 ${dotColor}`}></span>
      </span>
      <span className={`font-game text-[9px] tracking-wider ${textColor}`} data-testid="backend-status-label">
        {label}
      </span>
    </div>
  );
};

export default BackendStatusBadge;
